import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Image, Briefcase, Info } from "lucide-react";
import PortfolioRequestButton from "./PortfolioRequestButton";
import ExperienceRequestButton from "./ExperienceRequestButton";
import AboutRequestButton from "./AboutRequestButton";
import { ProfileData } from "@/v2/hooks/profiles/useProfiles";

interface EmptyStatePublicProps {
  type: "portfolio" | "experience" | "about";
  username?: string;
  profileId?: string;
  profile?: ProfileData;
}

const EmptyStatePublic = ({
  type,
  username = "Designer",
  profileId,
  profile,
}: EmptyStatePublicProps) => {
  const title =
    type === "portfolio"
      ? "Uh-oh! No portfolio projects yet."
      : type === "experience"
      ? "Looks like they haven't added any experience yet."
      : "No about information yet.";
  const description =
    type === "portfolio"
      ? `${username} hasn't added any of their awesome projects.`
      : type === "experience"
      ? `${username} hasn't added any experience yet. They might be missing out on great opportunities!`
      : `${username} hasn't shared any information about themselves yet. Request to learn more about them!`;

  return (
    <Card className="w-full bg-muted/30 border-dashed">
      <CardContent className="flex flex-col items-center justify-center p-12 text-center space-y-6">
        {type === "portfolio" ? (
          <div className="p-4 rounded-full flex items-center justify-center bg-transparent py-[16px]">
            <img
              src="/lovable-uploads/9e8df335-b81d-4efd-99b8-3e682345adcd.png"
              alt="No portfolio"
              className="h-24 w-24 object-contain"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
          </div>
        ) : type === "experience" ? (
          <div className="p-4 rounded-full flex items-center justify-center bg-transparent py-[16px]">
            <img
              src="/lovable-uploads/667f98fb-a40d-47d7-8ff5-68eebb9bbd8b.png"
              alt="No experience"
              className="h-24 w-24 object-contain"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
          </div>
        ) : (
          <div className="p-4 rounded-full flex items-center justify-center bg-transparent py-[16px]">
            <img
              src="/lovable-uploads/5b071fc7-c692-4fa9-a5d2-cc5e4176ae1f.png"
              alt="No about information"
              className="h-24 w-24 object-contain"
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
          </div>
        )}

        <div className="space-y-3 max-w-md">
          <h3 className="font-medium text-lg">{title}</h3>
          <p className="text-muted-foreground">{description}</p>

          {type === "portfolio" && username && profileId && (
            <PortfolioRequestButton
              username={username}
              profileId={profileId}
              profile={profile}
            />
          )}

          {type === "experience" && username && profileId && (
            <ExperienceRequestButton
              username={username}
              profileId={profileId}
              profile={profile}
            />
          )}

          {type === "about" && username && profileId && (
            <AboutRequestButton
              username={username}
              profileId={profileId}
              profile={profile}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EmptyStatePublic;
